import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

function Stars({ value, onChange, label }) {
  return (
    <div className="review-star-field">
      <label>{label}</label>
      <div className="review-stars" aria-label={`${label}: ${value || 0} out of 5`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button key={star} type="button" className={star <= Number(value || 0) ? "active" : ""} onClick={() => onChange?.(star)} disabled={!onChange} aria-label={`${star} star${star === 1 ? "" : "s"}`}>★</button>
        ))}
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
}

function ReviewCard({ review, user, onRespond, onModerate, busy }) {
  const [response, setResponse] = useState(review.merchant_response || review.transporter_response || "");
  const staff = user?.is_platform_staff || user?.is_superuser || ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"].includes(user?.account_type);
  const canRespond = ["SELLER", "BUSINESS", "COURIER"].includes(user?.account_type);
  return (
    <article className="marketplace-review-card">
      <header>
        <div>
          <span className={`review-status review-status--${String(review.status || "").toLowerCase()}`}>{review.status?.replaceAll("_", " ")}</span>
          <h3>{review.title}</h3>
          <small>{review.reviewer_name} • {formatDate(review.created_at)} {review.verified_purchase ? "• Verified purchase" : ""}</small>
        </div>
        <div className="review-card-score"><strong>{Number(review.product_rating || review.delivery_rating || 0).toFixed(1)}</strong><span>★</span></div>
      </header>
      <div className="review-subject-line">
        {review.product_slug ? <Link to={`/products/${review.product_slug}`}>{review.product_title}</Link> : <strong>{review.product_title || review.order_number}</strong>}
        <span>{review.order_number}</span>
      </div>
      <div className="review-rating-row">
        <span>Product <b>{review.product_rating}/5</b></span>
        <span>Seller <b>{review.seller_rating}/5</b></span>
        {review.delivery_rating ? <span>Delivery <b>{review.delivery_rating}/5</b></span> : null}
      </div>
      <p>{review.comment}</p>
      {review.merchant_response ? <div className="review-owner-response"><strong>Seller response</strong><p>{review.merchant_response}</p></div> : null}
      {review.transporter_response ? <div className="review-owner-response"><strong>Transporter response</strong><p>{review.transporter_response}</p></div> : null}
      <footer>
        <span>{review.helpful_count || 0} found this helpful</span>
        {staff && Number(review.report_count || 0) > 0 ? <strong>{review.report_count} report(s)</strong> : null}
      </footer>
      {canRespond ? (
        <div className="review-response-editor">
          <textarea value={response} onChange={(event) => setResponse(event.target.value)} placeholder="Respond professionally to this review" />
          <button type="button" disabled={!response.trim() || busy === `${review.id}-response`} onClick={() => onRespond(review, response)}>{busy === `${review.id}-response` ? "Saving..." : "Save response"}</button>
        </div>
      ) : null}
      {staff ? (
        <div className="review-moderation-actions">
          <button type="button" onClick={() => onModerate(review, "PUBLISH")}>Publish</button>
          <button type="button" onClick={() => onModerate(review, "REVIEW")}>Review</button>
          <button type="button" onClick={() => onModerate(review, "HIDE")}>Hide</button>
          <button type="button" className="danger" onClick={() => onModerate(review, "REJECT")}>Reject</button>
        </div>
      ) : null}
    </article>
  );
}

export default function ReviewsWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [form, setForm] = useState({ product_rating: 0, seller_rating: 0, delivery_rating: 0, title: "", comment: "" });

  const isBuyer = user?.account_type === "BUYER";
  const staff = user?.is_platform_staff || user?.is_superuser || ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"].includes(user?.account_type);

  async function load(nextFilter = filter) {
    setLoading(true);
    setError("");
    try {
      const requests = [phase1Api.reviews(nextFilter === "ALL" ? {} : { status: nextFilter })];
      if (isBuyer) requests.push(phase1Api.reviewEligibility());
      const [reviewRes, eligibilityRes] = await Promise.all(requests);
      setReviews(Array.isArray(reviewRes.data) ? reviewRes.data : []);
      setEligible(Array.isArray(eligibilityRes?.data) ? eligibilityRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Reviews could not be loaded right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [isBuyer]);

  const stats = useMemo(() => ({
    total: reviews.length,
    published: reviews.filter((item) => item.status === "PUBLISHED").length,
    attention: reviews.filter((item) => item.status === "UNDER_REVIEW" || Number(item.report_count || 0) > 0).length,
    average: reviews.length ? (reviews.reduce((sum, item) => sum + Number(item.product_rating || item.delivery_rating || 0), 0) / reviews.length).toFixed(1) : "0.0",
  }), [reviews]);

  function openReview(item) {
    setSelected(item);
    setForm({ product_rating: 0, seller_rating: 0, delivery_rating: 0, title: "", comment: "" });
    setError("");
  }

  async function submitReview(event) {
    event.preventDefault();
    if (!selected) return;
    setBusy("create");
    setError("");
    setMessage("");
    try {
      await phase1Api.createReview({
        order: selected.order,
        product: selected.product,
        product_rating: form.product_rating,
        seller_rating: form.seller_rating,
        delivery_rating: selected.courier_name && form.delivery_rating ? form.delivery_rating : null,
        title: form.title,
        comment: form.comment,
      });
      setSelected(null);
      setMessage("Thank you. Your verified purchase review is now visible on the marketplace.");
      await load();
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.detail || data?.title || data?.comment || data?.product_rating || "The review could not be submitted.");
    } finally {
      setBusy("");
    }
  }

  async function respond(review, response) {
    setBusy(`${review.id}-response`);
    setError("");
    try {
      await phase1Api.respondToReview(review.id, { response });
      setMessage("Response saved and the buyer has been notified.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.response || "Response could not be saved.");
    } finally {
      setBusy("");
    }
  }

  async function moderate(review, action) {
    setBusy(`${review.id}-${action}`);
    setError("");
    try {
      await phase1Api.moderateReview(review.id, { action });
      setMessage(`Review updated to ${action === "REVIEW" ? "under review" : action.toLowerCase()}.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Review moderation failed.");
    } finally {
      setBusy("");
    }
  }

  const heading = staff ? "Review moderation and marketplace trust" : isBuyer ? "My purchases and reviews" : user?.account_type === "COURIER" ? "Delivery ratings and responses" : "Customer reviews and responses";

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 reviews-workspace-main">
        <PortalTopbar user={user} onLogout={logout} />
        <section className="reviews-workspace-hero">
          <div><span>MARKETPLACE TRUST</span><h1>{heading}</h1><p>{isBuyer ? "Rate products, sellers and completed deliveries from purchases linked to your account." : staff ? "Monitor reported reviews and keep genuine buyer feedback useful and fair." : "See verified customer feedback and respond professionally from your own account."}</p></div>
          <div className="reviews-kpi-row"><div><strong>{stats.total}</strong><span>Reviews</span></div><div><strong>{stats.average}</strong><span>Average</span></div><div><strong>{stats.published}</strong><span>Published</span></div>{staff ? <div><strong>{stats.attention}</strong><span>Attention</span></div> : null}</div>
        </section>
        {message ? <div className="success-box workspace-alert">{message}</div> : null}
        {error ? <div className="error-box workspace-alert">{error}</div> : null}

        {isBuyer ? (
          <section className="review-eligibility-section">
            <div className="records-card__header"><div><span>COMPLETED PURCHASES</span><h3>Ready for your review</h3></div></div>
            <div className="review-eligibility-grid">
              {eligible.filter((item) => !item.already_reviewed).map((item) => (
                <article key={`${item.order}-${item.product}`}><span>{item.order_number}</span><h3>{item.product_title}</h3><p>Seller: {item.seller_name}</p>{item.courier_name ? <p>Delivered by: {item.courier_name}</p> : null}<button type="button" onClick={() => openReview(item)}>Write review</button></article>
              ))}
              {!loading && !eligible.some((item) => !item.already_reviewed) ? <div className="records-empty soft-empty">No completed purchases are waiting for a review.</div> : null}
            </div>
          </section>
        ) : null}

        <section className="reviews-list-section">
          <div className="records-card__header">
            <div><span>{staff ? "MODERATION QUEUE" : isBuyer ? "YOUR FEEDBACK" : "VERIFIED CUSTOMER FEEDBACK"}</span><h3>{staff ? "All marketplace reviews" : "Reviews"}</h3></div>
            {staff ? <select value={filter} onChange={(event) => { setFilter(event.target.value); load(event.target.value); }}><option value="ALL">All statuses</option><option value="PUBLISHED">Published</option><option value="UNDER_REVIEW">Under review</option><option value="HIDDEN">Hidden</option><option value="REJECTED">Rejected</option></select> : <button type="button" onClick={() => load()}>Refresh</button>}
          </div>
          {loading ? <div className="records-empty">Loading reviews...</div> : reviews.length ? <div className="marketplace-review-list">{reviews.map((review) => <ReviewCard key={review.id} review={review} user={user} onRespond={respond} onModerate={moderate} busy={busy} />)}</div> : <div className="records-empty">No reviews are available yet.</div>}
        </section>
      </main>

      {selected ? (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card review-compose-modal">
            <header className="tazy-modal-header"><div><span>VERIFIED PURCHASE</span><h3>Review {selected.product_title}</h3></div><button type="button" onClick={() => setSelected(null)}>×</button></header>
            <form onSubmit={submitReview}>
              <div className="review-compose-summary"><strong>{selected.order_number}</strong><span>{selected.seller_name}</span>{selected.courier_name ? <span>Delivered by {selected.courier_name}</span> : null}</div>
              <Stars label="Product quality" value={form.product_rating} onChange={(value) => setForm((prev) => ({ ...prev, product_rating: value }))} />
              <Stars label="Seller service" value={form.seller_rating} onChange={(value) => setForm((prev) => ({ ...prev, seller_rating: value }))} />
              {selected.courier_name ? <Stars label="Delivery service" value={form.delivery_rating} onChange={(value) => setForm((prev) => ({ ...prev, delivery_rating: value }))} /> : null}
              <div><label>Review title</label><input maxLength="140" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Summarise your experience" /></div>
              <div><label>Your review</label><textarea value={form.comment} onChange={(event) => setForm((prev) => ({ ...prev, comment: event.target.value }))} placeholder="What went well? What should other buyers know?" /></div>
              <p className="review-guidance">Keep feedback honest and based on this purchase. Do not include phone numbers or private information.</p>
              <div className="review-compose-actions"><button type="button" onClick={() => setSelected(null)}>Cancel</button><button type="submit" disabled={busy === "create" || !form.product_rating || !form.seller_rating || form.comment.trim().length < 10}>{busy === "create" ? "Publishing..." : "Publish review"}</button></div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
